import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Heart, User } from 'lucide-react';

/**
 * HEAREN V1.2 — TABBAR with integrated SOS Calm
 * 4 items: Home / SOS Calm (elevated center) / Favorites / Profile
 * SOS Calm has breathing glow, glass elevation, and wave icon
 */
const TabBar = ({ t }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasNotch, setHasNotch] = useState(false);

  const tabs = [
    {
      id: 'home',
      icon: Home,
      label: t?.tabHome || 'Home',
      path: '/',
      matchPaths: ['/', '/home'],
    },
    {
      id: 'favorites',
      icon: Heart,
      label: t?.tabFavorites || 'Favorites',
      path: '/favorites',
      matchPaths: ['/favorites', '/playlists'],
    },
    {
      id: 'profile',
      icon: User,
      label: t?.tabProfile || 'Profile',
      path: '/profile',
      matchPaths: ['/profile', '/history', '/tests', '/progress', '/coach'],
    },
  ];

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const hasNotchSupport = CSS.supports('padding-bottom: env(safe-area-inset-bottom)');
    setHasNotch(hasNotchSupport && isIOS);
  }, []);

  const isActive = (tab) => {
    return tab.matchPaths.some(path => {
      if (path === '/') return location.pathname === '/' || location.pathname === '/home';
      return location.pathname.startsWith(path);
    });
  };

  const isSosActive = location.pathname.startsWith('/sos');
  const accentColor = '#8B5CF6';

  // SOS Calm wave icon (inline SVG — no dependency)
  const SosIcon = ({ active }) => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12c1.5-3 4-5 6-5s3.5 2 6 2 4.5-2 6-2" stroke={active ? '#fff' : 'rgba(255,255,255,0.9)'} strokeWidth="2" opacity={active ? 1 : 0.85}/>
      <path d="M2 16.5c1.5-3 4-5 6-5s3.5 2 6 2 4.5-2 6-2" stroke={active ? '#fff' : 'rgba(255,255,255,0.9)'} strokeWidth="2" opacity={active ? 0.7 : 0.5}/>
    </svg>
  );

  // Render a standard tab
  const renderTab = (tab) => {
    const Icon = tab.icon;
    const active = isActive(tab);
    return (
      <button
        key={tab.id}
        onClick={() => navigate(tab.path)}
        className="flex flex-col items-center justify-center flex-1 h-full py-1 transition-all duration-150 active:scale-95"
        aria-label={tab.label}
        aria-current={active ? 'page' : undefined}
        data-testid={`tab-${tab.id}`}
      >
        <Icon
          size={22}
          color={active ? accentColor : 'rgba(255, 255, 255, 0.5)'}
          strokeWidth={active ? 2.2 : 1.8}
          className="transition-colors duration-150"
        />
        <span
          className="text-[10px] font-medium mt-1 transition-colors duration-150"
          style={{ color: active ? accentColor : 'rgba(255, 255, 255, 0.5)' }}
        >
          {tab.label}
        </span>
      </button>
    );
  };

  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-50"
      style={{ paddingBottom: '0' }}
      data-testid="tab-bar"
    >
      <div
        className="w-full relative"
        style={{
          background: 'rgba(15, 15, 25, 0.85)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div className="flex items-center justify-around h-[56px] px-2">
          {/* Home */}
          {renderTab(tabs[0])}

          {/* SOS Calm — elevated center */}
          <div className="flex flex-col items-center justify-center flex-1 h-full relative" style={{ marginTop: '-22px' }}>
            {/* Breathing halo glow */}
            <div
              className="absolute rounded-full pointer-events-none sos-tab-halo"
              style={{
                width: '76px',
                height: '76px',
                top: '-10px',
                background: 'radial-gradient(circle, rgba(99,102,241,0.35) 0%, rgba(139,92,246,0.15) 50%, transparent 72%)',
              }}
            />
            <button
              onClick={() => navigate('/sos')}
              className="relative flex items-center justify-center rounded-full sos-tab-button active:scale-95 transition-transform duration-150"
              style={{
                width: '54px',
                height: '54px',
                background: 'linear-gradient(145deg, rgba(79,70,229,0.9) 0%, rgba(124,58,237,0.85) 100%)',
                boxShadow: '0 4px 20px rgba(99,102,241,0.4), 0 0 0 1px rgba(165,180,252,0.2) inset, 0 1px 0 rgba(255,255,255,0.1) inset',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
              aria-label="SOS Calm"
              aria-current={isSosActive ? 'page' : undefined}
              data-testid="tab-sos-calm"
            >
              {/* Glass highlight */}
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 50%)',
                }}
              />
              <SosIcon active={isSosActive} />
            </button>
            <span
              className="text-[9px] font-semibold mt-1 tracking-wide transition-colors duration-150"
              style={{ color: isSosActive ? '#a5b4fc' : 'rgba(255,255,255,0.55)' }}
            >
              SOS Calm
            </span>
          </div>

          {/* Favorites */}
          {renderTab(tabs[1])}

          {/* Profile */}
          {renderTab(tabs[2])}
        </div>
      </div>
    </div>
  );
};

export default TabBar;
